import React, { createContext, useState, useCallback, ReactNode } from 'react';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastLink {
  to: string;
  label: string;
}

export interface Toast {
  id: string;
  variant: ToastVariant;
  message: string;
  /** Optional in-app link rendered under the message (e.g. → Approvals). */
  link?: ToastLink;
}

interface ToastContextValue {
  toasts: Toast[];
  push: (variant: ToastVariant, message: string, link?: ToastLink) => void;
  dismiss: (id: string) => void;
}

export const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (variant: ToastVariant, message: string, link?: ToastLink) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setToasts((prev) => [...prev, { id, variant, message, link }]);
      // Toasts with a link stick around longer — the user needs time to click.
      setTimeout(() => dismiss(id), link ? 8000 : 5000);
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={{ toasts, push, dismiss }}>
      {children}
    </ToastContext.Provider>
  );
};